import { useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import { coursesApi } from '../../api/courses'
import { reviewsApi } from '../../api/reviews'
import { subscriptionsApi } from '../../api/subscriptions'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import Button from '../../components/common/Button'
import YouTubeModal from '../../components/common/YouTubeModal'
import StarRating from '../../components/common/StarRating'
import SEO from '../../components/seo/SEO'
import { generateCourseStructuredData, generateBreadcrumbStructuredData } from '../../utils/seo'
import { FiVideo, FiFile, FiFileText, FiMail, FiShare2, FiFacebook, FiTwitter, FiLinkedin, FiSend, FiCalendar, FiClock, FiTag } from 'react-icons/fi'
import { FaGraduationCap } from 'react-icons/fa'
import './CourseDetail.css'

const CourseDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [activeTab, setActiveTab] = useState('about')
  const [showIntroVideo, setShowIntroVideo] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['course', id],
    queryFn: async () => {
      try {
        return await coursesApi.getCourse(id)
      } catch (err) {
        // Suppress console errors for expected 401 errors when not authenticated
        if (err?.response?.status === 401 && !isAuthenticated) {
          // This is expected - user needs to login
          throw err // Still throw to be handled by error state
        }
        throw err
      }
    },
    enabled: !!id,
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  // Fetch user subscriptions to check access
  const { data: subscriptionsData } = useQuery({
    queryKey: ['user-subscriptions'],
    queryFn: () => subscriptionsApi.getUserSubscriptions(),
    enabled: isAuthenticated && !!user,
  })

  // Check if user has access to this course.
  // Prefer backend `can_access` (strict package/course mapping).
  const canAccess = useMemo(() => {
    if (!isAuthenticated) return false

    if (typeof data?.data?.can_access === 'boolean') {
      return data.data.can_access
    }

    const course = data?.data
    if (!course || !subscriptionsData?.data?.subscriptions) return false

    const subscriptions = subscriptionsData.data.subscriptions
    const now = new Date()

    const activeSubs = subscriptions.filter((sub) => {
      if (sub.status !== 'active') return false
      if (!sub.expires_at) return false
      return new Date(sub.expires_at) > now
    })

    // Direct course subscription (coupon/per-course)
    if (activeSubs.some((sub) => sub.course_id && sub.course_id.toString() === id)) return true

    // Package subscriptions: must include course.id OR course.subject_id
    const subjectId = course?.subject_id || course?.subject?.id
    for (const sub of activeSubs) {
      const pkg = sub.package
      if (!pkg) continue

      const pkgCourseIds = (pkg.courses || []).map((c) => c.id)
      if (pkgCourseIds.includes(Number(id))) return true

      const pkgSubjectIds = (pkg.subjects || []).map((s) => s.id)
      if (subjectId && pkgSubjectIds.includes(Number(subjectId))) return true
    }

    return false
  }, [isAuthenticated, subscriptionsData, id, data?.data])

  const { data: unitsData } = useQuery({
    queryKey: ['course-units', id],
    queryFn: () => coursesApi.getCourseUnits(id),
    enabled: !!id && isAuthenticated && canAccess, // Only fetch when user can access
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  const { data: revisionData } = useQuery({
    queryKey: ['course-revision', id],
    queryFn: () => coursesApi.getRevisionLectures(id),
    enabled: !!id && activeTab === 'revision' && isAuthenticated && canAccess, // Only fetch when user can access
    retry: (failureCount, error) => {
      // Don't retry on 401 errors (unauthorized)
      if (error?.response?.status === 401) {
        return false
      }
      return failureCount < 2
    },
  })

  // Reviews are public, so this runs whether or not the visitor is signed in
  // — they are what someone reads while deciding whether to buy the course.
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ['course-reviews', id],
    queryFn: () => reviewsApi.getCourseReviews(id),
    enabled: !!id && activeTab === 'reviews',
    retry: false,
  })

  const reviews = reviewsData?.data?.reviews || []
  const ratingSummary = {
    rating: reviewsData?.data?.rating ?? null,
    count: reviewsData?.data?.reviews_count ?? 0,
  }
  const myReview = reviewsData?.data?.my_review || null
  // The server decides this, since it knows about packages and coupons as
  // well as direct subscriptions.
  const canReview = Boolean(reviewsData?.data?.can_review)

  const [reviewRating, setReviewRating] = useState(0)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewError, setReviewError] = useState('')
  const [isEditingReview, setIsEditingReview] = useState(false)

  const queryClient = useQueryClient()

  const submitReview = useMutation({
    mutationFn: () => reviewsApi.submitReview(id, {
      rating: reviewRating,
      comment: reviewComment.trim() || null,
    }),
    onSuccess: () => {
      setReviewError('')
      setIsEditingReview(false)
      // The new review is pending, so the list is unchanged but "my review"
      // is not — refetching is what shows the student their own words back.
      queryClient.invalidateQueries({ queryKey: ['course-reviews', id] })
    },
    onError: (err) => {
      setReviewError(
        err?.response?.data?.message ||
          'تعذّر إرسال التقييم. حاول مرة أخرى.'
      )
    },
  })

  const handleReviewSubmit = (event) => {
    event.preventDefault()

    if (reviewRating < 1) {
      setReviewError('اختر تقييمًا من 1 إلى 5 نجوم.')
      return
    }

    submitReview.mutate()
  }

  const startEditingReview = () => {
    setReviewRating(myReview?.rating || 0)
    setReviewComment(myReview?.comment || '')
    setReviewError('')
    setIsEditingReview(true)
  }

  // NOTE: `revisionData` is still fetched for the revision tab; keep the variable to avoid unused warnings.

  // Calculate statistics - must be before early returns (React Hooks rule)
  const units = unitsData?.data?.units || []
  const course = data?.data || {}

  const totalLectures = useMemo(() => {
    if (!units || units.length === 0) return course?.lectures_count || 0
    let count = 0
    units.forEach((unit) => {
      unit.chapters?.forEach((chapter) => {
        count += chapter.lectures?.length || 0
      })
    })
    return count
  }, [units, course?.lectures_count])

  const totalVideos = useMemo(() => {
    if (!units || units.length === 0) return course?.videos_count || 0
    let count = 0
    units.forEach((unit) => {
      unit.chapters?.forEach((chapter) => {
        chapter.lectures?.forEach((lecture) => {
          // Backend doesn't provide `lecture.type`; treat as video if it has a video relation.
          if (lecture?.video || (lecture?.videos && lecture.videos.length > 0)) count++
        })
      })
    })
    return count
  }, [units, course?.videos_count])

  const totalFiles = useMemo(() => {
    if (!units || units.length === 0) return course?.files_count || 0
    let count = 0
    units.forEach((unit) => {
      unit.chapters?.forEach((chapter) => {
        chapter.lectures?.forEach((lecture) => {
          // Backend uses lecture.materials for files/attachments.
          if ((lecture?.materials && lecture.materials.length > 0) || lecture?.type === 'file') count++
        })
      })
    })
    return count
  }, [units, course?.files_count])

  const totalExams = useMemo(() => {
    return course?.exams_count || 0
  }, [course?.exams_count])

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  // Handle 401 errors gracefully - show login required instead of error
  if (error?.response?.status === 401) {
    // Show login required banner and basic course info if available
    const course = data?.data || {}
    return (
      <div className="course-detail-page bg-gray-900 min-h-screen">
        <Header />
        <main className="course-detail-main bg-gray-900">
          <div className="container mx-auto px-4 py-8">
            {/* Login Required Banner */}
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى محتوى الكورس والاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
            
            {/* Show basic course info if available */}
            {course.title && (
              <div className="course-detail-layout">
                <div className="course-detail-sidebar">
                  {course.thumbnail && (
                    <div className="course-thumbnail-card">
                      <img
                        src={getStorageUrl(course.thumbnail)}
                        alt={course.title}
                        className="course-thumbnail-image"
                      />
                    </div>
                  )}
                  {/* Price Badge - Only show if course has a price (not free) */}
                  {course.price !== undefined && course.price > 0 && (
                    <div className="course-price-badge">
                      {`${course.price} ج.م`}
                    </div>
                  )}
                </div>
                <div className="course-detail-content">
                  {course.subject && (
                    <div className="course-breadcrumb">
                      <FaGraduationCap className="course-breadcrumb-icon" />
                      <span>{course.subject.name}</span>
                    </div>
                  )}
                  <h1 className="course-detail-title">{course.title}</h1>
                  {course.description && (
                    <div className="course-metadata">
                      <div
                        className="course-description-content text-gray-300"
                        dangerouslySetInnerHTML={{ __html: course.description }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (error || !data?.data) {
    return (
      <>
        <Header />
        <div className="error-container">
          <p>حدث خطأ أثناء تحميل الدورة</p>
        </div>
        <Footer />
      </>
    )
  }

  // Course data is available here (after error check)
  // NOTE: `course` is already defined above; reuse it here.

  const handlePlayButtonClick = () => {
    if (course.intro_video) {
      setShowIntroVideo(true)
    } else {
      // Navigate to first lecture if available
      if (canAccess && units.length > 0) {
        const firstUnit = units[0]
        if (firstUnit.chapters && firstUnit.chapters.length > 0) {
          const firstChapter = firstUnit.chapters[0]
          if (firstChapter.lectures && firstChapter.lectures.length > 0) {
            navigate(`/courses/${course.id}/lectures/${firstChapter.lectures[0].id}`)
          }
        }
      } else if (!isAuthenticated) {
        navigate('/login')
      }
    }
  }

  const handleShare = (platform) => {
    const url = window.location.href
    const text = `${course.title} - ${course.subject?.name || ''}`
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    }
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank')
    }
  }

  const shareUrl = window.location.href

  // Format dates
  const createdAt = course.created_at ? new Date(course.created_at).toLocaleDateString('ar-EG', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }) : null

  const updatedAt = course.updated_at ? new Date(course.updated_at).toLocaleDateString('ar-EG', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }) : null

  const siteUrl = 'https://3afer.net'
  const courseStructuredData = course.id ? generateCourseStructuredData(course, siteUrl) : null
  const breadcrumbData = course.id ? generateBreadcrumbStructuredData([
    { name: 'الرئيسية', url: '/' },
    { name: 'الدورات', url: '/courses' },
    { name: course.title || 'الدورة', url: `/courses/${course.id}` }
  ], siteUrl) : null

  const structuredDataArray = [courseStructuredData, breadcrumbData].filter(Boolean)

  return (
    <div className="course-detail-page bg-gray-900 min-h-screen">
      <SEO
        title={course.title}
        description={course.description || course.title}
        keywords={`${course.title}, ${course.subject?.name || ''}, دورات, كورسات, تعليم أونلاين`}
        image={course.thumbnail ? getStorageUrl(course.thumbnail) : undefined}
        url={`/courses/${course.id}`}
        type="website"
        structuredData={structuredDataArray.length > 0 ? structuredDataArray : undefined}
      />
      <Header />
      <main className="course-detail-main bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          {/* Login Required Banner */}
          {!isAuthenticated && (
            <div className="login-required-banner">
              <div className="login-required-content">
                <div className="login-required-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div className="login-required-text">
                  <p className="login-required-title">تسجيل الدخول مطلوب</p>
                  <p className="login-required-description">يجب تسجيل الدخول للوصول إلى محتوى الكورس والاشتراك</p>
                </div>
                <button 
                  className="login-required-button"
                  onClick={() => navigate('/login')}
                >
                  تسجيل الدخول
                </button>
              </div>
            </div>
          )}
          
          <div className="course-detail-layout">
            {/* Left Column - Course Summary & Actions */}
            <div className="course-detail-sidebar">
              {/* Course Thumbnail */}
              <div className="course-thumbnail-card">
                {course.thumbnail ? (
                  <div className="course-thumbnail-wrapper" onClick={handlePlayButtonClick}>
                    <img
                      src={getStorageUrl(course.thumbnail)}
                      alt={course.title}
                      className="course-thumbnail-image"
                    />
                    {course.intro_video && (
                      <div className="course-thumbnail-overlay">
                        <button className="course-play-button" aria-label="Play intro video">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="course-thumbnail-placeholder">
                    <FaGraduationCap className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Instructor Info */}
              {course.instructor && (
                <div className="course-instructor-info">
                  {course.instructor.avatar ? (
                    <img
                      src={getStorageUrl(course.instructor.avatar)}
                      alt={course.instructor.name}
                      className="course-instructor-avatar"
                    />
                  ) : (
                    <div className="course-instructor-avatar-placeholder">
                      {course.instructor.name?.[0]?.toUpperCase() || 'I'}
                    </div>
                  )}
                  <div className="course-instructor-text">
                    <p className="course-instructor-name">{course.instructor.name}</p>
                    <p className="course-instructor-subject">{course.subject?.name || 'عام'}</p>
                  </div>
                </div>
              )}

              {/* Price Badge - Only show if course has a price (not free) */}
              {!canAccess && course.price > 0 && (
                <div className="course-price-badge">
                  {`${course.price} ج.م`}
                </div>
              )}

              {/* Statistics */}
              <div className="course-statistics">
                <div className="course-stat-item">
                  <FiVideo className="course-stat-icon" />
                  <div className="course-stat-content">
                    <span className="course-stat-number">{totalVideos}</span>
                    <span className="course-stat-label">فيديو</span>
                  </div>
                </div>
                <div className="course-stat-item">
                  <FiFile className="course-stat-icon" />
                  <div className="course-stat-content">
                    <span className="course-stat-number">{totalFiles}</span>
                    <span className="course-stat-label">ملف</span>
                  </div>
                </div>
                <div className="course-stat-item">
                  <FiFileText className="course-stat-icon" />
                  <div className="course-stat-content">
                    <span className="course-stat-number">{totalLectures}</span>
                    <span className="course-stat-label">محاضرة</span>
                  </div>
                </div>
                <div className="course-stat-item">
                  <FiMail className="course-stat-icon" />
                  <div className="course-stat-content">
                    <span className="course-stat-number">{totalExams}</span>
                    <span className="course-stat-label">امتحان</span>
                  </div>
                </div>
              </div>

              {/* Enrollment/Continue Learning Button */}
              {canAccess ? (
                <button
                  className="course-enroll-button course-continue-button"
                  onClick={() => {
                    // Navigate to first lecture if available
                    if (units.length > 0) {
                      const firstUnit = units[0]
                      if (firstUnit.chapters && firstUnit.chapters.length > 0) {
                        const firstChapter = firstUnit.chapters[0]
                        if (firstChapter.lectures && firstChapter.lectures.length > 0) {
                          navigate(`/courses/${course.id}/lectures/${firstChapter.lectures[0].id}`)
                        }
                      }
                    }
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  <span>متابعة التعلم</span>
                </button>
              ) : (
                <button
                  className="course-enroll-button"
                  onClick={() => {
                    if (isAuthenticated) {
                      // Navigate to subscriptions page (package selection) instead of payment
                      navigate('/subscriptions')
                    } else {
                      navigate('/login')
                    }
                  }}
                >
                  اشترك الآن
                </button>
              )}

              {/* Share Section */}
              <div className="course-share-section">
                <p className="course-share-label">شير الكورس لصديقك</p>
                <p className="course-share-label-secondary">فيد غيرك بالشير</p>
                <div className="course-share-buttons">
                  <button onClick={() => navigator.share?.({ url: shareUrl, title: course.title }) || handleShare('facebook')} className="course-share-btn course-share-btn-active">
                    <FiShare2 />
                  </button>
                  <button onClick={() => handleShare('twitter')} className="course-share-btn">
                    <FiTwitter />
                  </button>
                  <button onClick={() => handleShare('linkedin')} className="course-share-btn">
                    <FiLinkedin />
                  </button>
                  <button onClick={() => handleShare('telegram')} className="course-share-btn">
                    <FiSend />
                  </button>
                  <button onClick={() => handleShare('facebook')} className="course-share-btn course-share-btn-active">
                    <FiFacebook />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Course Details */}
            <div className="course-detail-content">
              {/* Breadcrumb */}
              {course.subject && (
                <div className="course-breadcrumb">
                  <FaGraduationCap className="course-breadcrumb-icon" />
                  <span>{course.subject.name}</span>
                </div>
              )}

              {/* Course Title */}
              <h1 className="course-detail-title">{course.title}</h1>

              {/* Course Metadata */}
              <div className="course-metadata">
                <div className="course-metadata-stats">
                  <div className="course-metadata-stat">
                    <FiVideo className="course-metadata-icon" />
                    <span>{totalVideos} فيديو</span>
                  </div>
                  <div className="course-metadata-stat">
                    <FiFile className="course-metadata-icon" />
                    <span>{totalFiles} ملف</span>
                  </div>
                  <div className="course-metadata-stat">
                    <FiFileText className="course-metadata-icon" />
                    <span>{totalLectures} محاضرة</span>
                  </div>
                  <div className="course-metadata-stat">
                    <FiMail className="course-metadata-icon" />
                    <span>{totalExams} امتحان</span>
                  </div>
                </div>

                {createdAt && (
                  <div className="course-metadata-item">
                    <FiCalendar className="course-metadata-icon" />
                    <span>تاريخ انشاء الكورس {createdAt}</span>
                  </div>
                )}

                {updatedAt && (
                  <div className="course-metadata-item">
                    <FiClock className="course-metadata-icon" />
                    <span>آخر تحديث للكورس {updatedAt}</span>
                  </div>
                )}

                {course.price > 0 && course.discounted_price && course.discounted_price < course.price && (
                  <div className="course-metadata-item">
                    <FiTag className="course-metadata-icon" />
                    <span>سعر مخفض</span>
                  </div>
                )}
              </div>

              {/* Tabs Navigation */}
              <div className="course-detail-tabs">
                <button
                  className={`course-detail-tab ${activeTab === 'about' ? 'active' : ''}`}
                  onClick={() => setActiveTab('about')}
                >
                  وصف الكورس
                </button>
                <button
                  className={`course-detail-tab ${activeTab === 'lessons' ? 'active' : ''}`}
                  onClick={() => setActiveTab('lessons')}
                >
                  محتوى الكورس
                </button>
                <button
                  className={`course-detail-tab ${activeTab === 'instructor' ? 'active' : ''}`}
                  onClick={() => setActiveTab('instructor')}
                >
                  عن المعلم
                </button>
                <button
                  className={`course-detail-tab ${activeTab === 'reviews' ? 'active' : ''}`}
                  onClick={() => setActiveTab('reviews')}
                >
                  التقييمات
                </button>
              </div>

              {/* Tab Content */}
              <div className="course-detail-tab-content">
                {activeTab === 'about' && (
                  <div className="tab-content-wrapper">
                    <div
                      className="course-description-content text-gray-300"
                      dangerouslySetInnerHTML={{ __html: course.description || '' }}
                    />
                  </div>
                )}

                {activeTab === 'lessons' && (
                  <div className="tab-content-wrapper">
                    {units.length > 0 ? (
                      <div className="units-list">
                        {units.map((unit) => (
                          <div key={unit.id} className="unit-card">
                            <h3 className="unit-title">{unit.title}</h3>
                            {unit.description && (
                              <p className="unit-description">{unit.description}</p>
                            )}
                            <div className="chapters-list">
                              {unit.chapters.map((chapter) => (
                                <div key={chapter.id} className="chapter-item">
                                  <div className="chapter-header">
                                    <div className="chapter-item-content">
                                      <span className="chapter-title">{chapter.title}</span>
                                      {chapter.description && (
                                        <p className="chapter-description">{chapter.description}</p>
                                      )}
                                    </div>
                                    <span className="chapter-meta">
                                      {chapter.lectures_count || chapter.lectures?.length || 0} محاضرة
                                    </span>
                                  </div>
                                  {chapter.lectures && chapter.lectures.length > 0 && (
                                    <div className="lectures-list">
                                      {chapter.lectures.map((lecture) => (
                                        <div
                                          key={lecture.id}
                                          className={`lecture-item ${canAccess ? 'lecture-item-clickable' : ''}`}
                                          onClick={() => {
                                            if (canAccess) {
                                              navigate(`/courses/${course.id}/lectures/${lecture.id}`)
                                            } else if (!isAuthenticated) {
                                              navigate('/login')
                                            } else {
                                              // Take user to package selection
                                              navigate('/subscriptions')
                                            }
                                          }}
                                        >
                                          <div className="lecture-item-icon">
                                            {lecture.type === 'video' ? (
                                              <FiVideo className="lecture-icon" />
                                            ) : (
                                              <FiFile className="lecture-icon" />
                                            )}
                                          </div>
                                          <div className="lecture-item-content">
                                            <span className="lecture-title">{lecture.title}</span>
                                            {lecture.description && (
                                              <p className="lecture-description">{lecture.description}</p>
                                            )}
                                          </div>
                                          {lecture.duration && (
                                            <span className="lecture-duration">{lecture.duration}</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400">لا توجد وحدات متاحة</p>
                    )}
                  </div>
                )}

                {activeTab === 'instructor' && (
                  <div className="tab-content-wrapper">
                    {course.instructor ? (
                      <div className="instructor-detail-section">
                        {course.instructor.avatar && (
                          <img
                            src={getStorageUrl(course.instructor.avatar)}
                            alt={course.instructor.name}
                            className="instructor-detail-avatar"
                          />
                        )}
                        <h3 className="instructor-detail-name text-white text-2xl font-bold mb-4">{course.instructor.name}</h3>
                        {course.instructor.bio && (
                          <p className="instructor-detail-bio text-gray-300">{course.instructor.bio}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-400">لا توجد معلومات عن المدرب</p>
                    )}
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="tab-content-wrapper">
                    {reviewsLoading ? (
                      <Loading />
                    ) : (
                      <div className="course-reviews">
                        <div className="course-reviews-summary">
                          {ratingSummary.count > 0 ? (
                            <>
                              <span className="course-reviews-average">
                                {ratingSummary.rating.toFixed(1)}
                              </span>
                              <StarRating value={ratingSummary.rating} size="lg" />
                              <span className="course-reviews-count">
                                {ratingSummary.count} تقييم
                              </span>
                            </>
                          ) : (
                            /* No rating at all rather than a zero, which would
                               read as the worst possible score. */
                            <p className="course-reviews-empty">
                              لا توجد تقييمات بعد لهذه الدورة
                            </p>
                          )}
                        </div>

                        {canReview && (!myReview || isEditingReview) && (
                          <form className="course-review-form" onSubmit={handleReviewSubmit}>
                            <h4 className="course-review-form-title">
                              {myReview ? 'عدّل تقييمك' : 'قيّم هذه الدورة'}
                            </h4>
                            <StarRating
                              value={reviewRating}
                              onChange={setReviewRating}
                              size="lg"
                            />
                            <textarea
                              className="course-review-textarea"
                              rows={4}
                              maxLength={2000}
                              placeholder="اكتب رأيك في الدورة (اختياري)"
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                            />
                            {reviewError && (
                              <p className="course-review-error">{reviewError}</p>
                            )}
                            <div className="course-review-actions">
                              <button
                                type="submit"
                                className="course-review-submit"
                                disabled={submitReview.isPending}
                              >
                                {submitReview.isPending ? 'جارٍ الإرسال…' : 'إرسال التقييم'}
                              </button>
                              {isEditingReview && (
                                <button
                                  type="button"
                                  className="course-review-cancel"
                                  onClick={() => setIsEditingReview(false)}
                                >
                                  إلغاء
                                </button>
                              )}
                            </div>
                          </form>
                        )}

                        {!canReview && !myReview && (
                          <p className="course-review-locked">
                            {isAuthenticated
                              ? 'يمكنك تقييم الدورة بعد الاشتراك فيها.'
                              : 'سجّل الدخول واشترك في الدورة لتتمكن من تقييمها.'}
                          </p>
                        )}

                        <div className="course-reviews-list">
                          {/* The student's own review sits first, styled like
                              every other review: whether it is still pending
                              is not this list's business to say. */}
                          {myReview && !isEditingReview && (
                            <div className="course-review course-review-own">
                              <div className="course-review-head">
                                <span className="course-review-author">
                                  {myReview.user?.name || 'أنت'}
                                </span>
                                <StarRating value={myReview.rating} size="sm" />
                              </div>
                              {myReview.comment && (
                                <p className="course-review-comment">{myReview.comment}</p>
                              )}
                              {canReview && (
                                <button
                                  type="button"
                                  className="course-review-edit"
                                  onClick={startEditingReview}
                                >
                                  تعديل التقييم
                                </button>
                              )}
                            </div>
                          )}
                          {/* Once approved, the student's review is also in
                              this list — filtered out rather than shown twice. */}
                          {reviews
                            .filter((review) => !myReview || review.id !== myReview.id)
                            .map((review) => (
                            <div key={review.id} className="course-review">
                              <div className="course-review-head">
                                <span className="course-review-author">
                                  {review.user?.name || 'طالب'}
                                </span>
                                <StarRating value={review.rating} size="sm" />
                              </div>
                              {review.comment && (
                                <p className="course-review-comment">{review.comment}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      {/* YouTube Video Modal */}
      {course.intro_video && (
        <YouTubeModal
          videoUrl={course.intro_video}
          isOpen={showIntroVideo}
          onClose={() => setShowIntroVideo(false)}
        />
      )}
    </div>
  )
}

export default CourseDetail
